/**
 * Onboarding (/onboarding) — the two questions we cannot run the app without.
 *
 *   Step 1  name
 *   Step 2  board + grade  →  /start
 *
 * The school question is gone. It used to ask a student to type their school so
 * a slug could be matched against a teacher's, which meant spelling a name
 * correctly was enough to see that school's content. A school's students are now
 * provisioned BY the school and reach it through its own URL, so a self-reported
 * school name has nothing left to decide.
 *
 * Reached only after the email is confirmed (see components/AuthGate). Each
 * step saves as it is completed rather than batching to the end, so a student
 * who closes the tab between them comes back to a pre-filled form instead of
 * starting over — which, together with the `onboardedAt` stamp the API sets
 * once everything is answered, is what stops the questionnaire reappearing on
 * later sign-ins.
 *
 * Board and grade are written to the student's membership profile in the current
 * workspace — for a self-serve signup that is always the first-party tenant. The
 * account query is invalidated after the last save so the synchronous claims
 * cache picks up the new scope before the student is routed on.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiPut } from '@/lib/api';
import { invalidateAccount, invalidateProfile, useMyProfile } from '@/hooks/useSession';
import { useCurriculumLevels } from '@/hooks/useCurriculumLevels';
import { gradeLabel } from '@cwy/config/contracts';
import type { Board, Grade } from '@/types';
import { Button } from '@/components/ds';
import {
  AuthError,
  AuthHeading,
  AuthLayout,
  TextField,
} from '@/components/auth/AuthLayout';
import { fontPx } from '@/lib/fontSize';

// Only the board · grade pairs CWY has a syllabus for, from the API — a board a
// platform operator just created, with nothing under it yet, is not offered.

export function OnboardingPage() {
  const navigate = useNavigate();
  const levels = useCurriculumLevels();
  const cohorts = levels.data?.cohorts ?? [];
  const boardChoices = (levels.data?.boards ?? []).filter((b) =>
    cohorts.some((c) => c.board === b.code),
  );
  const profile = useMyProfile(true);

  const [step, setStep] = useState<1 | 2>(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [board, setBoard] = useState<Board | null>(null);
  const [grade, setGrade] = useState<Grade | null>(null);
  const [touched, setTouched] = useState(false);

  // Pre-fill from whatever was already answered. Runs once the profile lands;
  // the guard on `profile.data` keeps it from clobbering live typing on refetch.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || !profile.data) return;
    setFirstName(profile.data.firstName ?? '');
    setLastName(profile.data.lastName ?? '');
    setBoard(profile.data.board);
    setGrade(profile.data.grade);
    setHydrated(true);
  }, [profile.data, hydrated]);

  const saveDetails = useMutation({
    mutationFn: () =>
      apiPut('/me/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      }),
    onSuccess: () => {
      invalidateProfile();
      setTouched(false);
      setStep(2);
    },
  });

  const saveCurriculum = useMutation({
    mutationFn: async () => {
      await apiPut('/me/profile', { board, grade });
      // Board and grade live on the user row, so the write above is immediately
      // authoritative — there is no token to refresh. Both cached reads are
      // dropped so /start re-reads them rather than deciding on the copy that
      // still says onboarding is unfinished.
      invalidateAccount();
      invalidateProfile();
    },
    onSuccess: () => navigate('/start', { replace: true }),
  });

  const detailsComplete =
    firstName.trim().length > 0 && lastName.trim().length > 0;

  if (profile.isPending) {
    return (
      <AuthLayout width={640} step={1} totalSteps={2}>
        <p className="text-ink-soft">Loading your details…</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout width={640} step={step} totalSteps={2}>
      {step === 1 ? (
        <div className="flex flex-col gap-5">
          <AuthHeading title="First — what should we call you?">
            We use this to make the app yours, and to put your name on the reports you share.
          </AuthHeading>

          {saveDetails.isError && (
            <AuthError>
              {saveDetails.error instanceof Error
                ? saveDetails.error.message
                : 'Could not save your details. Please try again.'}
            </AuthError>
          )}

          <div className="flex flex-wrap gap-3">
            <TextField
              label="First name"
              autoFocus
              required
              maxLength={60}
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={touched && !firstName.trim() ? 'Please enter your first name.' : ''}
              containerStyle={{ flex: '1 1 160px' }}
            />
            <TextField
              label="Last name"
              required
              maxLength={60}
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={touched && !lastName.trim() ? 'Please enter your last name.' : ''}
              containerStyle={{ flex: '1 1 160px' }}
            />
          </div>

          <div>
            <Button
              style={{ padding: '11px 20px', fontSize: fontPx(15) }}
              disabled={saveDetails.isPending}
              onClick={() => {
                setTouched(true);
                if (detailsComplete) saveDetails.mutate();
              }}
            >
              {saveDetails.isPending ? 'Saving…' : 'Continue'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <AuthHeading title={firstName ? `Hi ${firstName}! Which board and grade?` : 'Which board and grade?'}>
            Everything you see — papers, questions, marking — is set to your curriculum. This
            can&rsquo;t be changed later, so pick carefully.
          </AuthHeading>

          <div>
            <p className="mb-2 text-sm font-semibold text-ink">Your board</p>
            <div className="grid grid-cols-2 gap-3">
              {boardChoices.map((b) => (
                <ChoiceCard
                  key={b.code}
                  active={board === b.code}
                  label={b.code}
                  note={b.label === b.code ? 'Board' : b.label}
                  onClick={() => {
                    setBoard(b.code);
                    // A grade this board does not teach cannot stay chosen.
                    if (grade && !cohorts.some((c) => c.board === b.code && c.grade === grade)) {
                      setGrade(null);
                    }
                  }}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-ink">Your grade</p>
            <div className="grid grid-cols-2 gap-3">
              {(levels.data?.grades ?? [])
                .filter((g) => cohorts.some((c) => c.board === board && c.grade === g.code))
                .map((g) => (
                  <ChoiceCard
                    key={g.code}
                    active={grade === g.code}
                    label={g.label}
                    note={`Class ${gradeLabel(g.code)} boards`}
                    onClick={() => setGrade(g.code)}
                  />
                ))}
            </div>
          </div>

          {saveCurriculum.isError && (
            <AuthError>
              {saveCurriculum.error instanceof Error
                ? saveCurriculum.error.message
                : 'Could not finish setting up. Please try again.'}
            </AuthError>
          )}

          <div className="flex gap-3">
            <Button variant="ghost" disabled={saveCurriculum.isPending} onClick={() => setStep(1)}>
              ← Back
            </Button>
            <Button
              style={{ padding: '11px 20px', fontSize: fontPx(15) }}
              disabled={!board || !grade || saveCurriculum.isPending}
              onClick={() => saveCurriculum.mutate()}
            >
              {saveCurriculum.isPending ? 'Setting up…' : 'Continue'}
            </Button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}

function ChoiceCard({
  active,
  label,
  note,
  onClick,
}: {
  active: boolean;
  label: string;
  note: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="rounded-card border-2 p-4 text-left transition"
      style={{
        borderColor: active ? 'var(--brand-forest)' : 'var(--border-subtle, var(--surface-sunk))',
        background: active ? 'var(--green-bg)' : 'var(--surface-card, #fff)',
      }}
    >
      <div
        className="text-base font-bold"
        style={{ color: active ? 'var(--brand-forest)' : 'var(--text-body)' }}
      >
        {label}
      </div>
      <div className="text-xs text-ink-soft">{note}</div>
    </button>
  );
}

export default OnboardingPage;